import { Checkbox, Collapse, makeStyles } from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import React, { useCallback, useState } from 'react'
import TreeNodeContentArea from './TreeNodeContentArea'
import type { SelectionTreeNode } from './SelectionTree'

const useStyles = makeStyles(() => ({
    row: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #E0E0E0',
        padding: `${pxToRem(2)} 0`,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#F5F5F5',
        },
    },
    checkbox: {
        padding: pxToRem(4),
        color: '#1565C0',
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            color: '#1565C0',
        },
    },
}))

/** Props for {@link TreeNodeRow}. */
export interface TreeNodeRowProps {
    /** The node to render. */
    node: SelectionTreeNode
    /** Nesting depth — used to compute left padding. */
    depth: number
    /** Set of currently selected leaf ids. */
    checkedSet: Set<string>
    /** Pre-computed map from node id to all its leaf descendant ids. */
    nodeLeafIdsMap: Map<string, string[]>
    /** Called with the clicked node id when the row or checkbox is activated. */
    onToggle: (key: string) => void
    /** When `true`, this node starts expanded. */
    defaultExpandAll: boolean
    /** Optional renderer for extra content shown to the right of each leaf node's label. */
    renderLeafExtra?: (nodeId: string) => React.ReactNode
}

/**
 * Renders a single row in the selection tree, including its checkbox, content area,
 * and (for parent nodes) a collapsible list of child rows.
 */
const TreeNodeRow = ({
    node,
    depth,
    checkedSet,
    nodeLeafIdsMap,
    onToggle,
    defaultExpandAll,
    renderLeafExtra,
}: TreeNodeRowProps) => {
    const classes = useStyles()
    const isParent = !!node.children?.length
    const [expanded, setExpanded] = useState(defaultExpandAll)
    const handleToggleExpand = useCallback(() => setExpanded((p) => !p), [])

    const leafIds = nodeLeafIdsMap.get(node.id) ?? []
    const selectedCount = leafIds.filter((id) => checkedSet.has(id)).length
    const total = leafIds.length
    const checked = total > 0 && selectedCount === total
    const indeterminate = selectedCount > 0 && selectedCount < total

    return (
        <>
            <div
                className={classes.row}
                style={{ paddingLeft: pxToRem(12 + depth * 24) }}
                onClick={() => onToggle(node.id)}
            >
                <Checkbox
                    size="small"
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() => onToggle(node.id)}
                    onClick={(e) => e.stopPropagation()}
                    className={classes.checkbox}
                    inputProps={{ 'aria-label': node.badge ? `${node.badge} ${node.label}` : node.label }}
                />
                <TreeNodeContentArea
                    node={node}
                    selectedCount={selectedCount}
                    total={total}
                    expanded={expanded}
                    onToggleExpand={handleToggleExpand}
                    renderLeafExtra={renderLeafExtra}
                />
            </div>
            {isParent && (
                <Collapse in={expanded} unmountOnExit>
                    {node.children!.map((child) => (
                        <TreeNodeRow
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            checkedSet={checkedSet}
                            nodeLeafIdsMap={nodeLeafIdsMap}
                            onToggle={onToggle}
                            defaultExpandAll={defaultExpandAll}
                            renderLeafExtra={renderLeafExtra}
                        />
                    ))}
                </Collapse>
            )}
        </>
    )
}

export default TreeNodeRow
